import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { API_BASE_URL } from '../api/config.js';
import { apiFetch } from '../api/request.js';
import { profileAPI } from '../services/api/profileAPI.js';
import StorageService from '../services/storage/storageService.js';
import { reloadPageForGoogleMapsLanguageChange } from '../utils/googleMapsLoader.js';
import { LANGUAGE_STORAGE_KEY, languageStore } from './languageStore.js';
import { changeI18nLanguage, translate } from './i18n.js';
import { createLocaleFormatters } from './formatters.js';
import {
  DEFAULT_LANGUAGE,
  filterSupportedLanguages,
  normalizeLanguageCode,
} from './localeManifest.js';
import LegacyInterfaceBridge from './LegacyInterfaceBridge.jsx';

const LanguageContext = createContext(null);

export const selectInitialLanguage = ({ languages, user, locallySaved }) => {
  const availableLanguages = filterSupportedLanguages(languages?.languages);
  if (availableLanguages.length === 0) {
    throw new Error('The backend language registry has no frontend-supported languages.');
  }
  const supported = new Set(availableLanguages.map((item) => item.code));
  const userLanguage = normalizeLanguageCode(user?.preferred_language);
  const savedLanguage = normalizeLanguageCode(locallySaved);
  const registryDefault = normalizeLanguageCode(languages?.default);
  if (supported.has(userLanguage)) return userLanguage;
  if (supported.has(savedLanguage)) return savedLanguage;
  if (supported.has(registryDefault)) return registryDefault;
  if (supported.has(DEFAULT_LANGUAGE)) return DEFAULT_LANGUAGE;
  return availableLanguages[0].code;
};

export const selectLocalBootstrapLanguage = ({ user, locallySaved }) => (
  normalizeLanguageCode(user?.preferred_language)
  || normalizeLanguageCode(locallySaved)
  || DEFAULT_LANGUAGE
);

export const LanguageProvider = ({ children }) => {
  const [localBootstrap] = useState(() => {
    const cachedUser = StorageService.get('userData');
    const locallySaved = StorageService.getItem(LANGUAGE_STORAGE_KEY);
    return {
      cachedUser,
      activeLanguage: selectLocalBootstrapLanguage({
        user: cachedUser,
        locallySaved,
      }),
    };
  });
  const [snapshot, setSnapshot] = useState(() => ({
    ...languageStore.getState(),
    activeLanguage: localBootstrap.activeLanguage,
  }));
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');
  const [changing, setChanging] = useState(false);
  const changeInFlight = useRef(false);
  const bootstrapGeneration = useRef(0);
  const bootstrapAbortController = useRef(null);
  const pendingExternalMapsChange = useRef(null);
  const [user, setUser] = useState(localBootstrap.cachedUser);

  useLayoutEffect(() => {
    const unsubscribe = languageStore.subscribe((nextSnapshot, change) => {
      if (change?.previousLanguage !== change?.nextLanguage) {
        if (change.source === 'apply-user') {
          const pending = pendingExternalMapsChange.current;
          pendingExternalMapsChange.current = {
            previousLanguage: pending?.previousLanguage || change.previousLanguage,
            nextLanguage: change.nextLanguage,
          };
        } else {
          // An interactive or bootstrap transition supersedes an external
          // profile change that React has not committed yet.
          pendingExternalMapsChange.current = null;
        }
      }
      setSnapshot(nextSnapshot);
    });
    // Layout effects run before the first paint and before the registry fetch
    // effect, so saved-English users never see a Romanian bootstrap screen.
    languageStore.primeActiveLanguage(localBootstrap.activeLanguage);
    return unsubscribe;
  }, [localBootstrap.activeLanguage]);

  useEffect(() => {
    const pending = pendingExternalMapsChange.current;
    if (!pending || pending.nextLanguage !== snapshot.activeLanguage) return;

    pendingExternalMapsChange.current = null;
    reloadPageForGoogleMapsLanguageChange(pending);
  }, [snapshot.activeLanguage]);

  const bootstrap = useCallback(async () => {
    bootstrapAbortController.current?.abort();
    const controller = new AbortController();
    const generation = ++bootstrapGeneration.current;
    bootstrapAbortController.current = controller;
    const isCurrentBootstrap = () => (
      !controller.signal.aborted && generation === bootstrapGeneration.current
    );

    setError('');
    setReady(false);
    try {
      const response = await apiFetch(
        `${API_BASE_URL}/languages/`,
        { signal: controller.signal },
        { auth: false },
      );
      if (!isCurrentBootstrap()) return;
      if (!response.ok) throw new Error(`Languages request failed (${response.status})`);
      const registry = await response.json();
      if (!isCurrentBootstrap()) return;
      if (!registry?.default || !Array.isArray(registry.languages) || registry.languages.length === 0) {
        throw new Error('Invalid languages response');
      }
      const cachedUser = StorageService.get('userData');
      const localLanguage = StorageService.getItem(LANGUAGE_STORAGE_KEY);
      const activeLanguage = selectInitialLanguage({
        languages: registry,
        user: cachedUser,
        locallySaved: localLanguage,
      });
      await changeI18nLanguage(activeLanguage);
      if (!isCurrentBootstrap()) return;
      languageStore.configure(registry, cachedUser, localLanguage);
      setUser(cachedUser);
      setReady(true);
    } catch (bootstrapError) {
      if (!isCurrentBootstrap() || bootstrapError?.name === 'AbortError') return;
      console.error('Language bootstrap failed:', bootstrapError);
      setError('bootstrap.failed');
    } finally {
      if (bootstrapAbortController.current === controller) {
        bootstrapAbortController.current = null;
      }
    }
  }, []);

  useEffect(() => {
    bootstrap();
    return () => {
      bootstrapGeneration.current += 1;
      bootstrapAbortController.current?.abort();
      bootstrapAbortController.current = null;
    };
  }, [bootstrap]);

  const syncUser = useCallback((nextUser) => {
    const synchronizedUser = languageStore.applyUser(nextUser);
    setUser(synchronizedUser || null);
  }, []);

  useEffect(() => {
    const handler = (event) => syncUser(event.detail?.user);
    window.addEventListener('atrux:user-language', handler);
    return () => window.removeEventListener('atrux:user-language', handler);
  }, [syncUser]);

  const changeLanguage = useCallback(async (next) => {
    const nextLanguage = normalizeLanguageCode(next);
    if (!nextLanguage || changeInFlight.current || nextLanguage === languageStore.getState().activeLanguage) return;
    if (!languageStore.getState().languages.some((item) => item.code === nextLanguage)) return;

    const previous = languageStore.getState().activeLanguage;
    const previousUser = user;
    changeInFlight.current = true;
    setChanging(true);
    languageStore.selectUserLanguage(nextLanguage);
    setUser(previousUser ? { ...previousUser, preferred_language: nextLanguage } : previousUser);

    try {
      // This second, awaited request joins the ordered i18next queue started by
      // the synchronous store update. It is the commit barrier for lazy bundles.
      await changeI18nLanguage(nextLanguage);
      const token = StorageService.getAuthToken();
      let updatedUser = previousUser;
      if (token) {
        updatedUser = await profileAPI.updateProfile({ preferred_language: nextLanguage }, token);
        updatedUser = languageStore.applyUser(updatedUser);
        StorageService.set('userData', updatedUser);
        setUser(updatedUser);
      }
      StorageService.setItem(LANGUAGE_STORAGE_KEY, nextLanguage);
      languageStore.selectUserLanguage(nextLanguage);
      await changeI18nLanguage(nextLanguage);
      reloadPageForGoogleMapsLanguageChange({
        previousLanguage: previous,
        nextLanguage,
      });
    } catch (changeError) {
      languageStore.selectUserLanguage(previous);
      await changeI18nLanguage(previous).catch((rollbackError) => {
        console.error(`Failed to restore i18n language "${previous}":`, rollbackError);
      });
      setUser(previousUser);
      if (previousUser) StorageService.set('userData', previousUser);
      StorageService.setItem(LANGUAGE_STORAGE_KEY, previous);
      throw changeError;
    } finally {
      changeInFlight.current = false;
      setChanging(false);
    }
  }, [user]);

  const value = useMemo(() => ({
    ...snapshot,
    ready,
    changing,
    user,
    changeLanguage,
    syncUser,
    t: (key, params) => translate(snapshot.activeLanguage, key, params),
    format: createLocaleFormatters(snapshot.activeLanguage),
  }), [snapshot, ready, changing, user, changeLanguage, syncUser]);

  if (!ready) {
    const message = translate(snapshot.activeLanguage, error || 'bootstrap.loading');
    return (
      <main className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <section className="max-w-md rounded-3xl bg-white border border-gray-200 p-8 text-center shadow-lg">
          <p className="text-neutral-600">{message}</p>
          {error && <button type="button" onClick={bootstrap} className="mt-5 rounded-xl bg-primary px-5 py-3 font-semibold text-white">{translate(snapshot.activeLanguage, 'common.retry')}</button>}
        </section>
      </main>
    );
  }

  return (
    <LanguageContext.Provider value={value}>
      <LegacyInterfaceBridge language={snapshot.activeLanguage} />
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used inside LanguageProvider');
  return context;
};

export const useLocaleFormat = () => useLanguage().format;
